import React, { useEffect, useMemo, useState } from 'react';
import {
  FiSearch, FiChevronDown, FiChevronRight, FiCopy, FiCheck,
  FiRefreshCw, FiAlertCircle, FiBookOpen,
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';
import p from './Pages.module.css';

/* ──────────────────────────────────────────────────────────────────────────────
 * Documentation page
 *
 * Renders the auto-generated integrator documentation served by the backend at
 * `GET /api/v1/documentation/` (admin or integration token). The endpoint
 * returns a JSON description of every integration-exposed endpoint (method,
 * path, auth, description, example response, etc.).
 *
 * The exact JSON shape is the backend's source of truth, so this view is
 * defensive: it normalises a few common shapes and falls back to a JSON dump
 * for anything it doesn't understand.
 * ──────────────────────────────────────────────────────────────────────────── */

const METHOD_COLORS = {
  GET:    { bg: 'rgba(115,201,145,0.18)', fg: '#73c991', bd: 'rgba(115,201,145,0.45)' },
  POST:   { bg: 'rgba(108,140,191,0.20)', fg: '#a8c1e8', bd: 'rgba(108,140,191,0.50)' },
  PUT:    { bg: 'rgba(212,160,82,0.20)',  fg: '#d4a052', bd: 'rgba(212,160,82,0.45)' },
  PATCH:  { bg: 'rgba(212,160,82,0.20)',  fg: '#d4a052', bd: 'rgba(212,160,82,0.45)' },
  DELETE: { bg: 'rgba(224,108,117,0.18)', fg: '#e06c75', bd: 'rgba(224,108,117,0.45)' },
};

function MethodBadge({ method = 'GET' }) {
  const c = METHOD_COLORS[method.toUpperCase()] || METHOD_COLORS.GET;
  return (
    <span style={{
      display: 'inline-block', minWidth: 56, textAlign: 'center',
      padding: '3px 8px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 700,
      background: c.bg, color: c.fg, border: `1px solid ${c.bd}`,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      letterSpacing: '0.5px',
    }}>{method.toUpperCase()}</span>
  );
}

function CopyButton({ text, title = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <button onClick={copy} title={title} style={{
      background: 'none', border: 'none', cursor: 'pointer',
      color: copied ? '#73c991' : 'var(--text-muted)', padding: 4,
      display: 'inline-flex', alignItems: 'center',
    }}>
      {copied ? <FiCheck size={13} /> : <FiCopy size={13} />}
    </button>
  );
}

function CodeBlock({ value, label }) {
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <div style={{ marginTop: 8 }}>
      {label && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase',
          letterSpacing: '0.5px', marginBottom: 4,
        }}>
          <span>{label}</span>
          <CopyButton text={text} />
        </div>
      )}
      <pre style={{
        margin: 0, padding: 12, borderRadius: 8,
        background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
        fontSize: '0.78rem', lineHeight: 1.5, color: 'var(--text-primary)',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        overflow: 'auto', maxHeight: 360,
      }}>{text}</pre>
    </div>
  );
}

function Pill({ children, color = 'rgba(255,255,255,0.06)', text = 'var(--text-secondary)' }) {
  return (
    <span style={{
      fontSize: '0.68rem', padding: '2px 8px', borderRadius: 999,
      background: color, color: text, border: '1px solid rgba(255,255,255,0.08)',
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

/* ── Normalisation ──
 * Accept several common documentation payload shapes:
 *   1. { endpoints: [...] }
 *   2. [...]                                // bare array
 *   3. { paths: { '/foo': { get: {...} } } } // openapi-ish
 * Each endpoint is normalised to:
 *   { method, path, summary, description, auth, params, requestBody,
 *     responseExample, statusCodes, tag, raw }
 */
function normalise(doc) {
  if (!doc) return [];
  // Shape 1 + 2
  const list = Array.isArray(doc) ? doc : (doc.endpoints || doc.routes || doc.items);
  if (Array.isArray(list)) return list.map(normaliseOne);
  // Shape 3 (openapi paths)
  if (doc.paths && typeof doc.paths === 'object') {
    const out = [];
    for (const [path, methods] of Object.entries(doc.paths)) {
      for (const [method, def] of Object.entries(methods || {})) {
        if (!['get', 'post', 'put', 'patch', 'delete'].includes(method.toLowerCase())) continue;
        out.push(normaliseOne({ method, path, ...def }));
      }
    }
    return out;
  }
  return [];
}

function normaliseOne(e) {
  const method = (e.method || e.httpMethod || 'GET').toUpperCase();
  const path = e.path || e.url || e.route || '';
  const tag = e.tag
    || (Array.isArray(e.tags) ? e.tags[0] : null)
    || derivedTag(path)
    || 'misc';
  const responseExample = e.responseExample
    || e.exampleResponse
    || e.example
    || e.response
    || e.responses?.['200']?.example
    || e.responses?.['201']?.example;
  return {
    method,
    path,
    summary: e.summary || e.title || e.name || '',
    description: e.description || e.docstring || '',
    auth: e.auth || e.authentication || e.security,
    params: e.params || e.parameters || e.queryParams,
    requestBody: e.requestBody || e.body || e.payload,
    responseSchema: e.responseSchema || null,
    responseDefinition: e.responseDefinition || null,
    responseExample,
    statusCodes: e.statusCodes || e.responses,
    tag,
    raw: e,
  };
}

function derivedTag(path) {
  if (!path) return null;
  // /api/v1/foo/... → "foo"; strip /api and /v\d+ prefixes.
  const parts = path.split('/').filter(Boolean).filter(p => p !== 'api' && !/^v\d+$/i.test(p));
  return parts[0] || null;
}

export default function Documentation() {
  const { token } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('ALL');
  const [openTags, setOpenTags] = useState({});

  const load = async () => {
    setLoading(true); setError(null);
    const candidates = ['/v1/documentation/', '/v1/documentation', '/v1/docs/'];
    const headers = { 'Accept': 'application/json', 'Content-Type': 'application/json' };
    if (token) headers['auth-token'] = token;
    let lastErr = null;
    for (const path of candidates) {
      try {
        const res = await fetch(API_BASE_URL + path, { headers });
        if (res.status === 401) {
          setError('Authentication required. Please sign in to view the API documentation.');
          setLoading(false);
          return;
        }
        if (!res.ok) throw new Error(`Error ${res.status}`);
        const data = await res.json();
        setDoc(data);
        setLoading(false);
        return;
      } catch (e) { lastErr = e; }
    }
    setError(lastErr?.message || 'Failed to load documentation');
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const endpoints = useMemo(() => normalise(doc), [doc]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = endpoints.filter(e => {
      if (methodFilter !== 'ALL' && e.method !== methodFilter) return false;
      if (!q) return true;
      return (
        e.path.toLowerCase().includes(q)
        || (e.summary || '').toLowerCase().includes(q)
        || (e.description || '').toLowerCase().includes(q)
        || (e.tag || '').toLowerCase().includes(q)
      );
    });
    const m = new Map();
    for (const e of filtered) {
      if (!m.has(e.tag)) m.set(e.tag, []);
      m.get(e.tag).push(e);
    }
    // Sort tags alphabetically, endpoints by path then method.
    return [...m.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([tag, items]) => [tag, items.sort((a, b) => (a.path + a.method).localeCompare(b.path + b.method))]);
  }, [endpoints, search, methodFilter]);

  const toggleTag = (tag) => setOpenTags(prev => ({ ...prev, [tag]: !prev[tag] }));

  return (
    <div className={p.page}>
      <div className={p.pageHeader}>
        <h1 className={p.pageTitle} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiBookOpen /> {doc?.title || 'API Documentation'}
        </h1>
        <p className={p.pageSubtitle}>
          {doc?.description || 'Auto-generated reference for every integration-exposed endpoint.'}{' '}
          Source: <code>{API_BASE_URL}/v1/documentation/</code>
        </p>
        {doc?.authentication && (
          <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Pill color="rgba(212,160,82,0.18)" text="#d4a052">
              Header: <strong>{doc.authentication.header}</strong>
            </Pill>
            <Pill color="rgba(108,140,191,0.18)" text="#a8c1e8">
              Scheme: {doc.authentication.scheme}
            </Pill>
            <Pill>User type: {doc.authentication.userType}</Pill>
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className={p.toolbar}>
        <div className={p.toolbarFilters}>
          <div style={{ position: 'relative' }}>
            <FiSearch style={{ position: 'absolute', left: 10, top: 11, color: 'var(--text-muted)', fontSize: '0.85rem' }} />
            <input
              className={p.searchInput}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search path, summary, description…"
              style={{ paddingLeft: 32, minWidth: 280 }}
            />
          </div>
          <select
            value={methodFilter}
            onChange={e => setMethodFilter(e.target.value)}
            className={p.searchInput}
            style={{ minWidth: 110 }}
          >
            {['ALL', 'GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <button
          className={`${p.btnOutline} ${p.btnSmall}`}
          onClick={load}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <FiRefreshCw size={13} /> Refresh
        </button>
      </div>

      {loading && (
        <div className={p.card} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          Loading documentation…
        </div>
      )}

      {error && !loading && (
        <div className={p.card} style={{
          borderColor: '#e06c75', background: 'rgba(224,108,117,0.06)',
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <FiAlertCircle color="#e06c75" size={18} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 600, color: '#e06c75', marginBottom: 4 }}>Failed to load documentation</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{error}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: 6 }}>
              Make sure the backend exposes <code>GET /api/v1/documentation/</code> and that you are signed in with an admin or integration token.
            </div>
          </div>
        </div>
      )}

      {!loading && !error && endpoints.length === 0 && (
        <div className={p.card} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          The documentation endpoint returned no entries. Raw response:
          <CodeBlock value={doc} />
        </div>
      )}

      {!loading && !error && grouped.map(([tag, items]) => {
        const open = openTags[tag] !== false; // default open
        return (
          <div key={tag} className={p.card} style={{ padding: 0, overflow: 'hidden' }}>
            <div
              onClick={() => toggleTag(tag)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px',
                cursor: 'pointer', userSelect: 'none',
                borderBottom: open ? '1px solid var(--border-color)' : 'none',
                background: 'var(--bg-primary)',
              }}
            >
              {open ? <FiChevronDown /> : <FiChevronRight />}
              <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)', textTransform: 'capitalize' }}>{tag}</h3>
              <Pill>{items.length} endpoint{items.length === 1 ? '' : 's'}</Pill>
            </div>
            {open && (
              <div>
                {items.map((e, i) => (
                  <EndpointRow key={`${e.method}-${e.path}-${i}`} ep={e} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function EndpointRow({ ep }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid var(--border-color)' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px',
          cursor: 'pointer', flexWrap: 'wrap',
        }}
      >
        {open ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
        <MethodBadge method={ep.method} />
        <code style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.85rem', color: 'var(--text-primary)', flex: 1, minWidth: 200,
        }}>{ep.path}</code>
        {ep.summary && (
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{ep.summary}</span>
        )}
        <CopyButton text={ep.path} title="Copy path" />
      </div>
      {open && <EndpointDetails ep={ep} />}
    </div>
  );
}

function EndpointDetails({ ep }) {
  return (
    <div style={{ padding: '4px 18px 18px 46px', background: 'var(--bg-secondary)' }}>
      {ep.description && (
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1.55, marginTop: 8 }}>
          {ep.description}
        </p>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
        {ep.auth && <Pill color="rgba(212,160,82,0.18)" text="#d4a052">auth: {String(typeof ep.auth === 'string' ? ep.auth : JSON.stringify(ep.auth))}</Pill>}
      </div>

      {ep.params && (Array.isArray(ep.params) ? ep.params.length > 0 : Object.keys(ep.params).length > 0) && (
        <ParamsTable label="Parameters" data={ep.params} />
      )}

      {ep.requestBody && (
        <CodeBlock label="Request body example" value={ep.requestBody} />
      )}

      {/* Side-by-side Definition / Example panels */}
      {(ep.responseDefinition || ep.responseExample) ? (
        <div style={{ marginTop: 12 }}>
          {ep.responseSchema && (
            <div style={{
              fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase',
              letterSpacing: '0.5px', marginBottom: 6,
            }}>Response schema: <span style={{ color: 'var(--text-secondary)', textTransform: 'none' }}>{ep.responseSchema}</span></div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={{
                fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase',
                letterSpacing: '0.5px', marginBottom: 4,
              }}>Definition (types)</div>
              <pre style={{
                margin: 0, padding: 12, borderRadius: 8,
                background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                fontSize: '0.78rem', lineHeight: 1.5, color: 'var(--text-primary)',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                overflow: 'auto', maxHeight: 360,
              }}>{ep.responseDefinition ? JSON.stringify(ep.responseDefinition, null, 2) : '(no response body)'}</pre>
            </div>
            <div>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase',
                letterSpacing: '0.5px', marginBottom: 4,
              }}>
                <span>Example (values)</span>
                {ep.responseExample && <CopyButton text={JSON.stringify(ep.responseExample, null, 2)} />}
              </div>
              <pre style={{
                margin: 0, padding: 12, borderRadius: 8,
                background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                fontSize: '0.78rem', lineHeight: 1.5, color: 'var(--text-primary)',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                overflow: 'auto', maxHeight: 360,
              }}>{ep.responseExample ? JSON.stringify(ep.responseExample, null, 2) : '(no response body)'}</pre>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 10, fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No response body</div>
      )}

      {ep.statusCodes && (
        <ParamsTable label="Status codes" data={ep.statusCodes} keyHeader="Code" />
      )}
    </div>
  );
}

function ParamsTable({ label, data, keyHeader = 'Name' }) {
  // Accept either array of {name, type, required, description} or object map.
  const rows = Array.isArray(data)
    ? data.map(r => ({ key: r.name || r.in || '', ...r }))
    : Object.entries(data).map(([k, v]) => ({ key: k, ...(typeof v === 'object' ? v : { description: String(v) }) }));
  if (rows.length === 0) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{
        fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase',
        letterSpacing: '0.5px', marginBottom: 4,
      }}>{label}</div>
      <div style={{
        border: '1px solid var(--border-color)', borderRadius: 8, overflow: 'hidden',
        background: 'var(--bg-primary)',
      }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '160px 100px 80px 1fr',
          fontSize: '0.7rem', color: 'var(--text-muted)', padding: '8px 12px',
          borderBottom: '1px solid var(--border-color)', textTransform: 'uppercase',
        }}>
          <span>{keyHeader}</span><span>Type</span><span>Required</span><span>Description</span>
        </div>
        {rows.map((r, i) => (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: '160px 100px 80px 1fr',
            fontSize: '0.78rem', padding: '8px 12px', color: 'var(--text-secondary)',
            borderBottom: i === rows.length - 1 ? 'none' : '1px solid var(--border-color)',
            alignItems: 'center',
          }}>
            <code style={{ color: 'var(--text-primary)' }}>{r.key}</code>
            <span>{r.type || r.dataType || '—'}</span>
            <span>{r.required ? 'yes' : 'no'}</span>
            <span>{r.description || r.desc || ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
